import React from "react";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

/** GFM requires a blank line before a table; without it, pipe-rows render as plain text. */
export function ensureBlankLineBeforeGFMTables(text) {
  const lines = String(text ?? "").split("\n");
  const out = [];
  const looksLikeTableRow = (line) => {
    const t = line.trim();
    return t.startsWith("|") && t.endsWith("|") && /[^|\s]/.test(t);
  };
  const looksLikeSeparatorRow = (line) => {
    const t = line.trim();
    if (!t.startsWith("|")) return false;
    return /^\|\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|\s*$/.test(t);
  };
  const isTableLine = (line) =>
    looksLikeTableRow(line) || looksLikeSeparatorRow(line);

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (isTableLine(line) && out.length > 0) {
      const prev = out[out.length - 1];
      if (prev !== "" && !isTableLine(prev)) {
        out.push("");
      }
    }
    out.push(line);
  }
  return out.join("\n");
}

const TABLE_BORDER = "1px solid #b4b4b4";
const CELL_STYLE = {
  border: TABLE_BORDER,
  padding: "8px 12px",
  textAlign: "left",
  verticalAlign: "top",
};

const clipboardMarkdownComponents = {
  h1: ({ node, children }) => (
    <h1
      style={{
        fontSize: "22px",
        fontWeight: 700,
        margin: "8px 0 12px",
        lineHeight: 1.3,
      }}
    >
      {children}
    </h1>
  ),
  h2: ({ node, children }) => (
    <h2
      style={{
        fontSize: "18px",
        fontWeight: 700,
        margin: "20px 0 8px",
        lineHeight: 1.35,
      }}
    >
      {children}
    </h2>
  ),
  h3: ({ node, children }) => (
    <h3
      style={{
        fontSize: "15px",
        fontWeight: 700,
        margin: "16px 0 6px",
        lineHeight: 1.35,
      }}
    >
      {children}
    </h3>
  ),
  p: ({ node, children }) => (
    <p style={{ margin: "0 0 10px", lineHeight: 1.5 }}>{children}</p>
  ),
  strong: ({ node, children }) => <strong>{children}</strong>,
  em: ({ node, children }) => <em>{children}</em>,
  ul: ({ node, children }) => (
    <ul style={{ margin: "0 0 12px", paddingLeft: "22px" }}>{children}</ul>
  ),
  ol: ({ node, children }) => (
    <ol style={{ margin: "0 0 12px", paddingLeft: "22px" }}>{children}</ol>
  ),
  li: ({ node, children }) => <li style={{ margin: "4px 0" }}>{children}</li>,
  hr: () => (
    <hr
      style={{
        border: "none",
        borderTop: "1px solid #ccc",
        margin: "16px 0",
      }}
    />
  ),
  a: ({ node, href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  ),
  table: ({ node, children }) => (
    <table
      border="1"
      cellPadding="6"
      cellSpacing="0"
      style={{
        borderCollapse: "collapse",
        width: "100%",
        margin: "12px 0",
        border: TABLE_BORDER,
      }}
    >
      {children}
    </table>
  ),
  thead: ({ node, children }) => <thead>{children}</thead>,
  tbody: ({ node, children }) => <tbody>{children}</tbody>,
  tr: ({ node, children }) => <tr>{children}</tr>,
  th: ({ node, children }) => (
    <th
      style={{
        ...CELL_STYLE,
        fontWeight: 700,
        background: "#f3f3f3",
      }}
    >
      {children}
    </th>
  ),
  td: ({ node, children }) => <td style={CELL_STYLE}>{children}</td>,
  pre: ({ node, children }) => (
    <pre
      style={{
        margin: "0 0 12px",
        padding: "10px 12px",
        background: "#f6f6f6",
        border: "1px solid #ddd",
        whiteSpace: "pre-wrap",
        fontFamily: "Consolas, Menlo, Monaco, monospace",
        fontSize: "13px",
      }}
    >
      {children}
    </pre>
  ),
  code: ({ node, children }) => (
    <code
      style={{
        fontFamily: "Consolas, Menlo, Monaco, monospace",
        fontSize: "0.9em",
      }}
    >
      {children}
    </code>
  ),
  blockquote: ({ node, children }) => (
    <blockquote
      style={{
        margin: "0 0 12px",
        padding: "4px 0 4px 12px",
        borderLeft: "3px solid #c8c8c8",
        color: "#444",
      }}
    >
      {children}
    </blockquote>
  ),
};

export function wrapClipboardHtml(fragment) {
  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<style>
  body { font-family: Calibri, Arial, sans-serif; font-size: 14px; color: #222; }
  table { border-collapse: collapse; width: 100%; }
  th, td { border: 1px solid #b4b4b4; padding: 8px 12px; text-align: left; vertical-align: top; }
  th { font-weight: 700; background: #f3f3f3; }
</style>
</head>
<body>
<!--StartFragment-->${fragment}<!--EndFragment-->
</body>
</html>`;
}

function normalizeClipboardPlainText(text) {
  return String(text ?? "")
    .replace(/\u00a0/g, " ")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function tableToPlainText(table) {
  const rows = [...table.querySelectorAll("tr")].map((tr) =>
    [...tr.querySelectorAll("th,td")].map((cell) =>
      (cell.innerText || cell.textContent || "").replace(/\s+/g, " ").trim(),
    ),
  );
  if (!rows.length) return "";

  const colCount = Math.max(...rows.map((row) => row.length), 1);
  const widths = Array.from({ length: colCount }, (_, i) =>
    Math.max(3, ...rows.map((row) => (row[i] || "").length)),
  );

  const formatRow = (cells) =>
    widths
      .map((width, i) => (cells[i] || "").padEnd(width, " "))
      .join("   ");

  const lines = [];
  const hasHeader = Boolean(table.querySelector("th"));
  rows.forEach((row, idx) => {
    lines.push(formatRow(row));
    if (idx === 0 && hasHeader) {
      lines.push(widths.map((width) => "-".repeat(width)).join("   "));
    }
  });
  return lines.join("\n");
}

function inlineClipboardText(node) {
  if (!node) return "";
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent || "";
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return "";

  const tag = node.tagName.toLowerCase();
  if (tag === "br") return "\n";
  if (tag === "pre") {
    return node.innerText || node.textContent || "";
  }

  return [...node.childNodes].map(inlineClipboardText).join("");
}

export function elementToClipboardPlainText(root) {
  if (!root) return "";

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return node.textContent || "";
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();
    switch (tag) {
      case "br":
        return "\n";
      case "h1":
      case "h2":
      case "h3":
      case "h4":
      case "h5":
      case "h6":
      case "p":
      case "blockquote":
        return `${inlineClipboardText(node).replace(/\s+\n/g, "\n").trim()}\n\n`;
      case "li": {
        const parent = node.parentElement;
        const isOl = parent?.tagName.toLowerCase() === "ol";
        const listItems = parent
          ? [...parent.children].filter(
              (child) => child.tagName.toLowerCase() === "li",
            )
          : [];
        const index = listItems.indexOf(node) + 1 || 1;
        const prefix = isOl ? `${index}. ` : "- ";
        const nested = [...node.children]
          .filter((child) => {
            const childTag = child.tagName.toLowerCase();
            return childTag === "ul" || childTag === "ol";
          })
          .map(walk)
          .join("");
        const clone = node.cloneNode(true);
        [...clone.children]
          .filter((child) => {
            const childTag = child.tagName.toLowerCase();
            return childTag === "ul" || childTag === "ol";
          })
          .forEach((child) => child.remove());
        const text = inlineClipboardText(clone).replace(/\s+/g, " ").trim();
        return `${prefix}${text}\n${nested}`;
      }
      case "ul":
      case "ol":
        return `${[...node.children].map(walk).join("")}\n`;
      case "pre":
        return `${(node.innerText || node.textContent || "").replace(/\n$/, "")}\n\n`;
      case "hr":
        return "----------\n\n";
      case "table":
        return `${tableToPlainText(node)}\n\n`;
      case "thead":
      case "tbody":
      case "tr":
      case "th":
      case "td":
        return "";
      default:
        return [...node.childNodes].map(walk).join("");
    }
  };

  return normalizeClipboardPlainText(
    [...root.childNodes].map(walk).join(""),
  );
}

export function markdownToClipboardContent(markdown) {
  const original = String(markdown ?? "").trim();
  const source = ensureBlankLineBeforeGFMTables(original);
  if (!source || typeof document === "undefined") {
    return { html: "", plain: original };
  }

  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:fixed;left:-9999px;top:0;width:800px;pointer-events:none;";
  document.body.appendChild(host);

  const root = createRoot(host);
  try {
    flushSync(() => {
      root.render(
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={clipboardMarkdownComponents}
        >
          {source}
        </ReactMarkdown>,
      );
    });
    const plain = elementToClipboardPlainText(host);
    return {
      html: wrapClipboardHtml(host.innerHTML),
      plain: plain || original,
    };
  } finally {
    root.unmount();
    host.remove();
  }
}

export function markdownToClipboardHtml(markdown) {
  return markdownToClipboardContent(markdown).html;
}
